/**
 * Utility to delete user accounts and all related data
 * Run this from browser console while logged in as admin
 */

import { db } from '../firebase/config';
import { 
  collection, 
  query, 
  where, 
  getDocs, 
  deleteDoc, 
  doc,
  writeBatch
} from 'firebase/firestore';

/**
 * Delete a user account and all related data
 * @param {string} email - Email of the user to delete
 */
export async function deleteUserByEmail(email) {
  console.log(`\n🔍 Finding user: ${email}`);
  
  try {
    // 1. Find the user document
    const usersRef = collection(db, 'users');
    const userQuery = query(usersRef, where('email', '==', email));
    const userSnapshot = await getDocs(userQuery);
    
    if (userSnapshot.empty) {
      console.log(`❌ User not found: ${email}`);
      return { success: false, message: 'User not found' };
    }
    
    const userDoc = userSnapshot.docs[0];
    const userId = userDoc.id;
    const userData = userDoc.data();
    
    console.log(`✅ Found user: ${userData.name || 'N/A'} (ID: ${userId})`);
    console.log(`   - Referral Code: ${userData.referralCode || 'N/A'}`);
    console.log(`   - Referred By: ${userData.referredBy || 'None'}`);
    
    const deletedItems = [];
    
    // 2. Delete user's commissions (where they earned)
    console.log(`\n📊 Deleting commissions earned by user...`);
    const commissionsRef = collection(db, 'commissions');
    const userCommissionsQuery = query(commissionsRef, where('userId', '==', userId));
    const userCommissions = await getDocs(userCommissionsQuery);
    
    for (const commDoc of userCommissions.docs) {
      await deleteDoc(doc(db, 'commissions', commDoc.id));
      deletedItems.push(`Commission: ${commDoc.id}`);
    }
    console.log(`   Deleted ${userCommissions.size} commission records`);
    
    // 3. Delete commissions generated from this user's purchases (where they were the source)
    console.log(`\n📊 Deleting commissions from user's purchases...`);
    const sourceCommissionsQuery = query(commissionsRef, where('sourceUserId', '==', userId));
    const sourceCommissions = await getDocs(sourceCommissionsQuery);
    
    for (const commDoc of sourceCommissions.docs) {
      await deleteDoc(doc(db, 'commissions', commDoc.id));
      deletedItems.push(`Source Commission: ${commDoc.id}`);
    }
    console.log(`   Deleted ${sourceCommissions.size} source commission records`);
    
    // 4. Delete user's orders (cart orders)
    console.log(`\n🛒 Deleting cart orders...`);
    const cartOrdersRef = collection(db, 'cartOrders');
    const cartOrdersQuery = query(cartOrdersRef, where('userId', '==', userId));
    const cartOrders = await getDocs(cartOrdersQuery);
    
    for (const orderDoc of cartOrders.docs) {
      await deleteDoc(doc(db, 'cartOrders', orderDoc.id));
      deletedItems.push(`Cart Order: ${orderDoc.id}`);
    }
    console.log(`   Deleted ${cartOrders.size} cart orders`);
    
    // 5. Delete user's package purchases
    console.log(`\n📦 Deleting package purchases...`);
    const packagesRef = collection(db, 'packagePurchases');
    const packagesQuery = query(packagesRef, where('userId', '==', userId));
    const packages = await getDocs(packagesQuery);
    
    for (const pkgDoc of packages.docs) {
      await deleteDoc(doc(db, 'packagePurchases', pkgDoc.id));
      deletedItems.push(`Package: ${pkgDoc.id}`);
    }
    console.log(`   Deleted ${packages.size} package purchases`);
    
    // 6. Delete user's referral stats
    console.log(`\n📈 Deleting referral stats...`);
    const referralStatsRef = collection(db, 'referralStats');
    const referralStatsQuery = query(referralStatsRef, where('userId', '==', userId));
    const referralStats = await getDocs(referralStatsQuery);
    
    for (const statDoc of referralStats.docs) {
      await deleteDoc(doc(db, 'referralStats', statDoc.id));
      deletedItems.push(`Referral Stats: ${statDoc.id}`);
    }
    console.log(`   Deleted ${referralStats.size} referral stat records`);
    
    // 7. Delete cashback records
    console.log(`\n💰 Deleting cashback records...`);
    const cashbackRef = collection(db, 'cashbackRecords');
    const cashbackQuery = query(cashbackRef, where('userId', '==', userId));
    const cashback = await getDocs(cashbackQuery);
    
    for (const cbDoc of cashback.docs) {
      await deleteDoc(doc(db, 'cashbackRecords', cbDoc.id));
      deletedItems.push(`Cashback: ${cbDoc.id}`);
    }
    console.log(`   Deleted ${cashback.size} cashback records`);
    
    // 8. Finally, delete the user document
    console.log(`\n👤 Deleting user document...`);
    await deleteDoc(doc(db, 'users', userId));
    deletedItems.push(`User: ${userId}`);
    console.log(`   ✅ User document deleted`);
    
    console.log(`\n✅ Successfully deleted user ${email} and all related data`);
    console.log(`   Total items deleted: ${deletedItems.length}`);
    
    return {
      success: true,
      userId,
      email,
      deletedItems,
      totalDeleted: deletedItems.length
    };
    
  } catch (error) {
    console.error(`❌ Error deleting user ${email}:`, error);
    return { success: false, message: error.message };
  }
}

/**
 * Delete multiple users by email
 * @param {string[]} emails - Array of emails to delete
 */
export async function deleteMultipleUsers(emails) {
  console.log('='.repeat(60));
  console.log('🗑️  USER ACCOUNT DELETION UTILITY');
  console.log('='.repeat(60));
  console.log(`Users to delete: ${emails.length}`);
  
  const results = [];
  
  for (const email of emails) {
    console.log('\n' + '-'.repeat(60));
    const result = await deleteUserByEmail(email);
    results.push(result);
  }
  
  console.log('\n' + '='.repeat(60));
  console.log('📋 DELETION SUMMARY');
  console.log('='.repeat(60));
  
  for (const result of results) {
    if (result.success) {
      console.log(`✅ ${result.email} - Deleted (${result.totalDeleted} items)`);
    } else {
      console.log(`❌ ${result.email} - Failed: ${result.message}`);
    }
  }
  
  return results;
}

// Make it available in window for console access
if (typeof window !== 'undefined') {
  window.deleteUserByEmail = deleteUserByEmail;
  window.deleteMultipleUsers = deleteMultipleUsers;
}

export default { deleteUserByEmail, deleteMultipleUsers };

